import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  Text,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Badge,
  Button,
  Spinner,
  Alert,
  AlertIcon,
  Image
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useDataStore } from '../../contexts/DataStoreContext';

export const EventList = () => {
  const { getAllEvents, getActiveEvents, loading } = useDataStore();
  const [liveEvents, setLiveEvents] = useState([]);
  const [pastEvents, setPastEvents] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    organizeEvents();
  }, [getActiveEvents]);

  const organizeEvents = () => {
    const activeEvents = getActiveEvents();
    const now = new Date();

    // Separate events into live/upcoming and past
    const live = [];
    const past = [];

    activeEvents.forEach(event => {
      const eventDate = event.date instanceof Date ? event.date : new Date(event.date);
      // Compare dates only (ignore time) to keep events active through the entire day
      const eventDateOnly = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate());
      const todayOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      if (eventDateOnly >= todayOnly) {
        live.push(event);
      } else {
        past.push(event);
      }
    });

    // Sort live events by date (soonest first)
    live.sort((a, b) => {
      const dateA = a.date instanceof Date ? a.date : new Date(a.date);
      const dateB = b.date instanceof Date ? b.date : new Date(b.date);
      return dateA.getTime() - dateB.getTime();
    });

    // Sort past events by date (most recent first)
    past.sort((a, b) => {
      const dateA = a.date instanceof Date ? a.date : new Date(a.date);
      const dateB = b.date instanceof Date ? b.date : new Date(b.date);
      return dateB.getTime() - dateA.getTime();
    });

    setLiveEvents(live);
    setPastEvents(past);
  };

  const handleEventSelect = (eventId) => {
    navigate(`/event/${eventId}`);
  };

  if (loading.events) {
    return (
      <Box textAlign="center" py={10}>
        <Spinner size="xl" color="blue.500" />
        <Text mt={4} color="gray.300">Loading events...</Text>
      </Box>
    );
  }

  const renderEventCard = (event, isLive = true) => (
    <Card
      key={event.id}
      cursor="pointer"
      _hover={{ shadow: 'md', transform: 'translateY(-2px)' }}
      transition="all 0.2s"
      overflow="hidden"
      onClick={() => handleEventSelect(event.id)}
    >
      {/* Cover Image */}
      {event.coverImage && (
        <Box>
          <Image
            src={event.coverImage}
            alt={`${event.name} cover image`}
            width="100%"
            height="200px"
            objectFit="cover"
          />
        </Box>
      )}

      <CardHeader>
        <VStack align="start" spacing={2}>
          <Heading size="md">{event.name}</Heading>
          <Text>{event.location}</Text>
          <Text color="gray.400">
            {event.date instanceof Date
              ? event.date.toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit'
              })
              : 'Date TBD'
            }
          </Text>
          <Badge colorScheme={isLive ? "green" : "gray"}>
            {isLive ? "Live" : "Past Event"}
          </Badge>
        </VStack>
      </CardHeader>
      <CardBody pt={0}>
        <VStack align="stretch" spacing={3}>
          {event.description && (
            <Text fontSize="sm" noOfLines={2}>
              {event.description}
            </Text>
          )}
          <Text fontSize="sm" color="gray.400">
            {event.fights.length} fights scheduled
          </Text>
          <Button
            colorScheme="blue"
            size="sm"
          >
            {isLive ? "Vote on Fights" : "View Results"}
          </Button>
        </VStack>
      </CardBody>
    </Card>
  );

  return (
    <Box maxW="4xl" mx="auto" p={6}>
      {/* Live/Upcoming Events Section */}
      <Heading mb={6} textAlign="center">
        Live & Upcoming Events
      </Heading>

      <VStack spacing={4} align="stretch" mb={8}>
        {liveEvents.map(event => renderEventCard(event, true))}

        {liveEvents.length === 0 && (
          <Card>
            <CardBody textAlign="center" py={10}>
              <Text color="gray.400" mb={4}>
                No live or upcoming events at the moment
              </Text>
              <Text fontSize="sm" color="gray.500">
                Check back later or scan a QR code at an event to participate!
              </Text>
            </CardBody>
          </Card>
        )}
      </VStack>

      {/* Past Events Section */}
      {pastEvents.length > 0 && (
        <>
          <Heading mb={6} textAlign="center" size="lg">
            Past Events
          </Heading>

          <VStack spacing={4} align="stretch">
            {pastEvents.map(event => renderEventCard(event, false))}
          </VStack>
        </>
      )}
    </Box>
  );
};
